<template>
  <v-dialog v-model="dialog" :fullscreen="true" hide-overlay transition="dialog-bottom-transition">
    <v-card>
      <v-toolbar dark color="primary">
        <v-btn icon dark @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t('message.request_activation') }}</v-toolbar-title>
      </v-toolbar>

      <v-stepper v-model="e1" class="elevation-0">
        <v-stepper-header>
          <v-stepper-step :complete="Number(e1) > 1" step="1" editable>
            <template v-if="item.startsAt && item.endsAt && e1 > 1">
              {{ item.startsAt | moment('D MMM YY') }} -
              {{
                $moment(item.endsAt)
                  .subtract(1, 'days')
                  .format('D MMM YY')
              }}
            </template>
            <template v-else>{{ $t('message.select_activation_days') }}</template>
          </v-stepper-step>

          <template v-if="!initialAirtimePlan">
            <v-divider></v-divider>

            <v-stepper-step :complete="Number(e1) > 2" step="2" :editable="item.startsAt && item.endsAt ? true : false">
              <template v-if="item.airtimePlan && e1 > 2">{{ item.airtimePlan.name }}</template>
              <template v-else>{{ $t('message.choose_your_connection_speed') }}</template>
            </v-stepper-step>
          </template>

          <v-divider></v-divider>

          <v-stepper-step :complete="Number(e1) > (initialAirtimePlan ? 2 : 3)" :step="initialAirtimePlan ? 2 : 3">{{
            $t('message.summary')
          }}</v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step :step="initialAirtimePlan ? 3 : 4">{{ $t('message.payment') }}</v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-container>
            <v-stepper-content class="pa-0" step="1">
              <ActivationDays
                v-if="Number(e1) === 1 && agreement"
                :agreement="agreement"
                :item="item"
                :airtime-balances-merged="airtimeBalancesMerged"
                :initial-airtime-plan="initialAirtimePlan"
                @close="$emit('close')"
                @updateItem="updateItem"
                @updateStep="e1 = $event"
              />
              <v-row justify="center" align="center" no-gutters class="mt-12">
                <v-progress-circular v-if="agreementLoading" indeterminate color="primary" />
              </v-row>
            </v-stepper-content>

            <v-stepper-content v-if="!initialAirtimePlan" class="pa-0" step="2">
              <ConnectionSpeed
                v-if="Number(e1) === 2"
                :agreement="agreement"
                :item="item"
                :airtime-balances-merged="airtimeBalancesMerged"
                @updateItem="updateItem"
                @updateStep="e1 = $event"
              />
            </v-stepper-content>

            <v-stepper-content class="pa-0" :step="initialAirtimePlan ? 2 : 3">
              <Summary
                v-if="Number(e1) === (initialAirtimePlan ? 2 : 3)"
                :agreement="agreement"
                :item="item"
                :has-initial-airtime-plan="!!initialAirtimePlan"
                :airtime-balances-merged="airtimeBalancesMerged"
                @updateStep="e1 = $event"
              />
            </v-stepper-content>

            <v-stepper-content class="pa-0" :step="initialAirtimePlan ? 3 : 4">
              <Payment
                v-if="Number(e1) === (initialAirtimePlan ? 3 : 4)"
                :agreement="agreement"
                :item="item"
                :has-initial-airtime-plan="!!initialAirtimePlan"
                @updateStep="e1 = $event"
                @periodCreated="$emit('periodCreated', $event)"
                @paymentCreated="$emit('paymentCreated', $event)"
              />
            </v-stepper-content>
          </v-container>
        </v-stepper-items>
      </v-stepper>
    </v-card>
  </v-dialog>
</template>

<script>
import fetch from '@/utils/fetch'
import ActivationDays from '@/components/ActivationCreateDialog/ActivationDays'
import ConnectionSpeed from '@/components/ActivationCreateDialog/ConnectionSpeed'
import Summary from '@/components/ActivationCreateDialog/Summary'
import Payment from '@/components/ActivationCreateDialog/Payment'

export default {
  name: 'ActivationCreateDialog',
  components: { Payment, Summary, ConnectionSpeed, ActivationDays },
  props: {
    agreementId: {
      type: String,
      required: true
    },
    startsAt: {
      type: Date,
      required: false,
      default: null
    },
    endsAt: {
      type: Date,
      required: false,
      default: null
    },
    reset: {
      type: Boolean,
      default: false
    },
    show: {
      type: Boolean,
      default: false
    },
    initialAirtimePlan: {
      type: Object,
      required: false,
      default: null
    }
  },

  data: function() {
    return {
      e1: 1,
      item: {
        startsAt: null,
        endsAt: null,
        airtimePlan: null,
        pricingDescription: '',
        type: 'activation'
      },
      agreement: null,
      agreementLoading: false
    }
  },
  computed: {
    dialog: {
      get: function() {
        return this.show
      },
      set: function(newValue) {
        if (newValue === false) {
          this.$emit('close')
        }
        return this.show
      }
    },

    airtimeBalancesMerged() {
      let remainingDays = []

      if (!this.agreement) {
        return remainingDays
      }

      this.agreement.airtimeBalances.forEach(balance => {
        let i = null

        remainingDays.forEach((remainingDay, index) => {
          if (remainingDay.airtimePlan['@id'] === balance.airtimePlan['@id']) {
            i = index
          }
        })

        if (null !== i) {
          remainingDays[i].remainingDays += balance.remainingDays
        } else {
          remainingDays.push({
            airtimePlan: balance.airtimePlan,
            remainingDays: balance.remainingDays
          })
        }
      })

      return remainingDays
    }
  },

  watch: {
    dialog(dialog) {
      if (dialog) {
        this.fetchServiceAgreement(this.agreementId)
        this.e1 = 1
      }
    },
    startsAt(startsAt) {
      if (!startsAt) {
        return
      }

      this.item.startsAt = this.$moment(startsAt).format('YYYY-MM-DD') + ' 08:00:00'

      this.e1 = 1
    },

    endsAt(endsAt) {
      if (!endsAt) {
        return
      }

      this.item.endsAt = this.$moment(endsAt).format('YYYY-MM-DD') + ' 08:00:00'

      this.e1 = 1
    },

    initialAirtimePlan(airtimePlan) {
      this.item.airtimePlan = airtimePlan
      this.e1 = 1
    }
  },

  created() {
    if (this.startsAt) {
      this.item.startsAt = this.$moment(this.startsAt).format('YYYY-MM-DD') + ' 08:00:00'
    }

    if (this.endsAt) {
      this.item.endDate = this.$moment(this.endsAt).format('YYYY-MM-DD') + ' 08:00:00'
    }
  },

  mounted() {
    this.fetchServiceAgreement(this.agreementId)
    this.item.agreement = this.agreementId
    this.item.airtimePlan = this.initialAirtimePlan
  },

  methods: {
    fetchServiceAgreement: function(agreementId) {
      this.agreement = null
      this.agreementLoading = true
      fetch(agreementId)
        .then(response => {
          return response.json()
        })
        .then(data => {
          this.agreement = data
          this.agreementLoading = false
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
          this.agreementLoading = false
        })
    },

    resetItem() {
      this.item = {
        startsAt: null,
        endsAt: null,
        airtimePlan: null,
        pricingDescription: ''
      }
    },

    updateItem(item) {
      this.item = item
    }
  }
}
</script>
